import { useQuery } from "@tanstack/react-query";
import { APIErrorResponse } from "src/types/generic";
import apiClient from "../apiClient";

export type ProfitAndLossColumn = {
  branch_id: number | null;
  name: string;
  column_scope?: "branch" | "company" | "total";
};

export type ProfitAndLossRow = {
  key: string;
  label: string;
  amounts: number[];
  is_bold: boolean;
  row_type: "line" | "invoice" | "header";
  // Hierarchy metadata — present only on chart-account rows. Rows arrive
  // pre-ordered depth-first (a parent is immediately followed by its
  // descendants); `depth` drives indentation and `parent_key` links a row to
  // its parent row's `key`.
  chart_account_id?: number | null;
  parent_chart_account_id?: number | null;
  parent_key?: string | null;
  depth?: number;
  has_children?: boolean;
  is_expandable?: boolean;
};

export type ProfitAndLossSection = {
  key: string;
  title: string;
  rows: ProfitAndLossRow[];
};

export type ProfitAndLossSummaryRow = {
  key: string;
  label: string;
  amounts: number[];
  is_bold: boolean;
  row_type: "currency" | "percent";
};

export type ProfitAndLossReportPayload = {
  start_date: string;
  end_date: string;
  branch_id: number | null;
  columns: ProfitAndLossColumn[];
  sections: ProfitAndLossSection[];
  summary: ProfitAndLossSummaryRow[];
};

interface IProfitAndLossParams {
  start_date: string;
  end_date: string;
  branch_id?: number;
  enabled?: boolean;
}

export const useGetProfitAndLossReportData = (
  params: IProfitAndLossParams,
) =>
  useQuery<any, APIErrorResponse, { data: ProfitAndLossReportPayload }>({
    queryKey: [
      "get-profit-and-loss-report-data",
      params.start_date,
      params.end_date,
      params.branch_id,
      params.enabled ?? true,
    ],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.set("start_date", params.start_date);
      searchParams.set("end_date", params.end_date);
      if (params.branch_id != null) {
        searchParams.set("branch_id", String(params.branch_id));
      }
      return apiClient.get(
        `/api/reports/profit-and-loss?${searchParams.toString()}`,
      );
    },
    enabled: params.enabled !== false,
    gcTime: 0,
  });
